import { OTS_STAFF_DELIVERY_ICON_SRC, OTS_STAFF_PICKUP_ICON_SRC, OTS_STAFF_STATUS_ICON_SRC } from '../constants/ots-staff-status-icons';
import { OTS_STAFF_STATUS_LABELS } from '../constants/ots-staff-status-labels';
import { IOtsStaffOrder } from '../interfaces/IOtsStaffOrder';
import { IOtsStaffOrderCard } from '../interfaces/IOtsStaffOrderCard';
import { OtsLocationKind } from '../types';
import { OtsStaffEuroUtils } from './OtsStaffEuroUtils';

export class OtsStaffOrderCardUtils {

  public static format(order: IOtsStaffOrder, isMoving = false): IOtsStaffOrderCard {
    const statusPair = OtsStaffOrderCardUtils.statusPair(order);
    const actionPair = OtsStaffOrderCardUtils.actionPair(order);
    const minutes = Math.max(0, Math.floor((Date.now() - order.createdAt) / 60000));

    return {
      id: order.id,
      numberLabel: `#${order.id}`,
      status: order.status,
      statusLabel: statusPair[0].trans(statusPair[1]),
      statusIconSrc: OTS_STAFF_STATUS_ICON_SRC[order.status],
      metaLine: minutes === 0
        ? 'Just now · [[name]]'.trans('ots.staff.meta.now', [['name', order.customerName]])
        : '[[minutes]] min ago · [[name]]'.trans('ots.staff.meta', [
          ['minutes', minutes],
          ['name', order.customerName]
        ]),
      locationLine: OtsStaffOrderCardUtils.locationLine(order.locationTitle, order.locationKind, order.seatNumber),
      locationIconSrc: order.fulfillmentId === 'delivery' ? OTS_STAFF_DELIVERY_ICON_SRC : OTS_STAFF_PICKUP_ICON_SRC,
      fulfillmentId: order.fulfillmentId,
      fulfillmentLabel: order.fulfillmentId === 'delivery'
        ? 'Delivery'.trans('ots.menu.delivery')
        : 'Pickup'.trans('ots.staff.fulfillment.pickup'),
      lines: order.lines.map((line, index) => ({
        lineKey: `${line.productId}:${index}`,
        productId: line.productId,
        label: '[[qty]]× [[name]]'.trans('ots.staff.line', [
          ['qty', line.qty],
          ['name', line.name.trans(line.nameKey)]
        ]),
        amountLabel: OtsStaffEuroUtils.format(line.unitPriceCents * line.qty),
        packageProductLabels: line.packageProductNames
      })),
      actionLabel: actionPair ? actionPair[0].trans(actionPair[1]) : null,
      isMoving
    };
  }

  private static statusPair(order: IOtsStaffOrder): readonly [string, string] {
    if (order.status !== 'ready') {
      return OTS_STAFF_STATUS_LABELS[order.status];
    }

    if (order.fulfillmentId === 'delivery') {
      return OTS_STAFF_STATUS_LABELS.ready;
    }

    return ['Ready for Pickup', 'ots.staff.status.ready_pickup'];
  }

  private static actionPair(order: IOtsStaffOrder): readonly [string, string] | null {
    if (order.status === 'ready') {
      return order.fulfillmentId === 'delivery'
        ? ['Mark Delivered', 'ots.staff.action.delivered']
        : ['Mark Received', 'ots.staff.action.received'];
    }

    if (order.status === 'preparing') {
      return order.fulfillmentId === 'delivery'
        ? ['Mark Ready', 'ots.staff.action.ready']
        : ['Mark Ready for Pickup', 'ots.staff.action.ready_pickup'];
    }

    if (order.status === 'new') {
      return ['Start Preparing', 'ots.staff.action.prepare'];
    }

    return null;
  }

  private static locationLine(
    locationTitle: string,
    kind: OtsLocationKind | undefined,
    seatNumber: string
  ): string {
    const seat = seatNumber.trim();
    if (!seat) {
      return locationTitle;
    }

    return `${locationTitle} · ${OtsStaffOrderCardUtils.placeLabel(kind)} ${seat}`;
  }

  private static placeLabel(kind: OtsLocationKind | undefined): string {
    return kind === 'restaurant'
      ? 'Table'.trans('ots.fulfillment.table')
      : 'Seat'.trans('ots.fulfillment.seat');
  }
}
